import { motion } from "framer-motion";
import { Users, Calculator, Sparkles } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const CARDS = [
    {
        icon: Users,
        t: "Sustained participation",
        d: "UXT participation is measured through qualifying balance and holding duration — sustained participation carries more weight than a last-minute purchase.",
        tid: "uxt-card-participation",
    },
    {
        icon: Calculator,
        t: "Genesis score",
        d: "UXT Genesis Score = qualifying UXT balance × qualifying holding duration.",
        tid: "uxt-card-score",
    },
    {
        icon: Sparkles,
        t: "Genesis allocation",
        d: "Sustained holders earn allocation from the 50,000 TNX UXT Holder Genesis pool, announced on 28 October 2026.",
        tid: "uxt-card-allocation",
    },
];

export default function UxtExplainer() {
    return (
        <section id="uxt" data-testid="uxt-section" className="relative overflow-hidden bg-void py-24 lg:py-32">
            <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
                <div className="mx-auto max-w-3xl text-center">
                    <Reveal>
                        <Eyebrow className="justify-center">UXT & TNX</Eyebrow>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <h2 className="font-grotesk text-2xl font-bold uppercase leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl">
                            From UXT <span className="text-grad-blue">to TNX.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.14}>
                        <p className="mt-5 text-base leading-relaxed text-slate-400">
                            Two tokens, one ecosystem — here is how the connection works for
                            newcomers.
                        </p>
                    </Reveal>
                </div>

                {/* token flow */}
                <Reveal delay={0.2}>
                    <div
                        data-testid="uxt-flow"
                        className="mx-auto mt-14 flex max-w-2xl items-center justify-center gap-5 sm:gap-10"
                    >
                        <div className="flex flex-col items-center gap-3">
                            <div className="flex h-20 w-20 items-center justify-center rounded-full border border-slate-500/40 bg-[radial-gradient(120%_120%_at_30%_20%,#2a3648_0%,#121826_60%,#05070C_100%)] shadow-[0_0_40px_rgba(148,163,184,0.15)] sm:h-24 sm:w-24">
                                <span className="font-grotesk text-sm font-bold tracking-[0.2em] text-slate-300">
                                    UXT
                                </span>
                            </div>
                            <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-slate-500">
                                Participation
                            </span>
                        </div>

                        <div className="relative top-[-14px] h-px w-16 sm:w-32" data-testid="uxt-flow-uxt">
                            <div className="absolute inset-x-0 top-1/2 border-t border-dashed border-cyber/40" />
                            <motion.div
                                className="absolute top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-cyber shadow-[0_0_10px_rgba(0,209,255,0.9)]"
                                animate={{ left: ["0%", "96%"], opacity: [0, 1, 1, 0] }}
                                transition={{ duration: 2.4, repeat: Infinity, ease: "linear" }}
                            />
                        </div>

                        <div className="flex flex-col items-center gap-3">
                            <img
                                src={`${process.env.PUBLIC_URL}/brand/tvx-coin-hero.png`}
                                alt="TNX"
                                loading="lazy"
                                draggable={false}
                                data-testid="uxt-flow-tvx"
                                className="h-20 w-20 select-none object-contain drop-shadow-[0_0_36px_rgba(0,209,255,0.4)] sm:h-24 sm:w-24"
                            />
                            <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-cyber/80">
                                Ecosystem utility
                            </span>
                        </div>
                    </div>
                </Reveal>

                <div className="mx-auto mt-12 grid max-w-5xl gap-3 sm:grid-cols-3">
                    {CARDS.map((c, i) => (
                        <Reveal key={c.t} delay={0.1 + i * 0.08}>
                            <div
                                data-testid={c.tid}
                                className="group h-full rounded-lg border border-white/[0.07] bg-panel/40 p-6 transition-all duration-500 hover:border-cyber/40 hover:bg-panel/70"
                            >
                                <c.icon className="h-5 w-5 text-cyber" strokeWidth={1.5} />
                                <h3 className="mt-5 font-grotesk text-sm font-semibold uppercase tracking-[0.14em] text-white">
                                    {c.t}
                                </h3>
                                <p className="mt-2.5 text-[13px] leading-relaxed text-slate-400">{c.d}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <Reveal delay={0.3}>
                    <p className="mx-auto mt-10 max-w-2xl text-center font-mono text-[10px] uppercase leading-relaxed tracking-[0.18em] text-slate-600">
                        Summary based on the TNX Genesis Tokenomics Proposal — see official
                        documentation for the full rules. Not financial advice.
                    </p>
                </Reveal>
            </div>
        </section>
    );
}
